/**
 * 设置网页水印（支持固定数量模式）
 * @param {Object} options - 配置参数
 * @param {HTMLElement} options.container - 水印的父容器DOM元素
 * @param {string} options.watermarkText - 水印内容（支持换行，用\n分隔）
 * @param {string} [options.density='medium'] - 密集程度：'low'/'medium'/'high'（对应密度等级）
 * @param {string} [options.color='#ffffff'] - 水印颜色
 * @param {string} [options.fontSize='12px'] - 水印字体大小
 * @param {number} [options.angle=-25] - 水印倾斜角度
 * @param {number} [options.opacity=1] - 水印透明度
 *
 * @param {string} [options.mode='gap'] - 模式：'gap' / 'count' (默认：'gap') gap 通过更改间距来实现水印密度，count 通过数量来实现水印密度
 * @returns {Object} - 包含更新水印、移除水印的方法
 */

// 默认密度等级对应固定数量（可通过参数覆盖）
const defaultFixedCountMap = {
  low: 3,
  medium: 5,
  high: 10,
};
const gapSpacingMap = {
  low: 400,
  medium: 300,
  high: 200,
};
export function setWatermark({
  container,
  watermarkText,
  density = "medium",
  color = "#ffffff",
  fontSize = "12px",
  angle = -25,
  opacity = 1,
  fixedCountMap = defaultFixedCountMap,
  mode = "gap", // gap: 间距模式 / count: 固定数量模式
}) {
  // 1. 参数校验
  if (!(container instanceof HTMLElement)) {
    throw new Error("参数container必须是有效的DOM元素");
  }
  if (typeof watermarkText !== "string" || watermarkText.trim() === "") {
    throw new Error("水印内容watermarkText必须是非空字符串");
  }

  // 处理密度与模式
  const normalizeDensity = (value) =>
    ["low", "medium", "high"].includes(value) ? value : "medium";
  let densityLevel = normalizeDensity(density);
  let currentMode = mode === "count" ? "count" : "gap";
  let fixedCount = fixedCountMap[densityLevel] || defaultFixedCountMap.medium;

  // 2. 初始化水印容器（避免重复创建）
  let watermarkContainer = container.querySelector(".watermark-container");
  if (!watermarkContainer) {
    watermarkContainer = document.createElement("div");
    watermarkContainer.className = "watermark-container";
    Object.assign(watermarkContainer.style, {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      pointerEvents: "none",
      zIndex: 9999,
      overflow: "hidden",
    });
    if (getComputedStyle(container).position === "static") {
      container.style.position = "relative";
    }
    container.appendChild(watermarkContainer);
  }

  // 3. 计算水印尺寸与布局（支持两种模式）
  const calculateLayout = () => {
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    // 固定水印尺寸（不随容器大小变化）
    const watermarkWidth = 240;
    const watermarkHeight = 120;

    if (currentMode === "count") {
      const rowCount = Math.ceil(Math.sqrt(fixedCount)); // 行数≈平方根（均匀分布）
      const colCount = Math.ceil(fixedCount / rowCount); // 列数=总数/行数
      const watermarkWidth = Math.max(240, containerWidth / (colCount * 1.5)); // 最小宽度60px
      const watermarkHeight = Math.max(120, containerHeight / (rowCount * 1.5)); // 最小高度30px
      const gapX = containerWidth / colCount; // 横向间距=容器宽度/列数
      const gapY = containerHeight / rowCount; // 纵向间距=容器高度/行数
      return {
        watermarkWidth,
        watermarkHeight,
        gapX,
        gapY,
        rowCount: rowCount,
        colCount: colCount,
      };
    } else {
      // 密度等级模式：间距由密集程度决定（不依赖容器大小）
      const spacing = gapSpacingMap[densityLevel] || gapSpacingMap.medium;

      const gapX = spacing;
      const gapY = spacing;

      // 根据间距和容器大小计算能容纳多少行和列
      const rowCount = Math.ceil(containerHeight / gapY) + 1;
      const colCount = Math.ceil(containerWidth / gapX) + 1;

      return {
        watermarkWidth,
        watermarkHeight,
        gapX,
        gapY,
        rowCount,
        colCount,
      };
    }
  };

  // 4. 生成单个水印元素
  const createWatermarkItem = (width, height) => {
    const item = document.createElement("div");
    // console.log("fontSize___", fontSize);
    Object.assign(item.style, {
      position: "absolute",
      width: `${width}px`,
      height: `${height}px`,
      color: color,
      fontSize: fontSize,
      transform: `rotate(${angle}deg)`,
      transformOrigin: "center",
      opacity: opacity,
      textAlign: "center",
      whiteSpace: "pre-wrap",
      userSelect: "none",
      textStroke: "0.3px rgba(0,0,0,0.2)",
      "-webkit-text-stroke": "0.3px rgba(0,0,0,0.2)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center", // 文字垂直居中
    });
    // console.log("createWatermarkItem", item);
    // item.textContent = watermarkText;
    item.innerHTML = watermarkText;
    return item;
  };

  // 5. 渲染水印（核心逻辑）
  const renderWatermark = () => {
    watermarkContainer.innerHTML = "";
    const { watermarkWidth, watermarkHeight, gapX, gapY, rowCount, colCount } =
      calculateLayout();
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;

    let renderCount = 0;
    const maxCount = currentMode === "count" ? fixedCount : rowCount * colCount;

    // 错开偏移量：奇数行向右偏移，偶数行不偏移（或相反）
    const staggerOffset = gapX * 0.5; // 错开距离为间距的一半

    // 循环生成水印（固定数量模式下限制总数）
    for (let i = 0; i < rowCount; i++) {
      for (let j = 0; j < colCount; j++) {
        if (renderCount >= maxCount) break;

        const watermarkItem = createWatermarkItem(
          watermarkWidth,
          watermarkHeight
        );

        // 错开排列：奇数行（i % 2 === 1）向右偏移，偶数行不偏移
        const staggerX = i % 2 === 1 ? staggerOffset : 0;

        // 优化定位：从中心偏移，避免水印贴边，并加上错开偏移
        const offsetX =
          j * gapX - (colCount * gapX - containerWidth) / 2 + staggerX;
        const offsetY = i * gapY - (rowCount * gapY - containerHeight) / 2;

        watermarkItem.style.left = `${offsetX}px`;
        watermarkItem.style.top = `${offsetY}px`;
        watermarkContainer.appendChild(watermarkItem);
        renderCount++;
      }
      if (renderCount >= maxCount) break;
    }
  };

  // 6. 防删除保护
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (
        mutation.removedNodes.length > 0 &&
        Array.from(mutation.removedNodes).includes(watermarkContainer)
      ) {
        container.appendChild(watermarkContainer);
        renderWatermark();
      } else if (watermarkContainer.innerHTML === "") {
        renderWatermark();
      }
    });
  });
  observer.observe(container, { childList: true, subtree: true });
  observer.observe(watermarkContainer, { childList: true });

  // 7. 监听容器尺寸变化（使用 ResizeObserver）
  let resizeObserver = null;
  let resizeTimer = null;
  const handleResize = () => {
    // 使用防抖优化性能，避免频繁重新渲染
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      renderWatermark();
    }, 100);
  };

  // 检查浏览器是否支持 ResizeObserver
  if (typeof ResizeObserver !== "undefined") {
    resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(container);
  } else {
    // 降级方案：使用 window resize 事件（仅支持窗口大小变化）
    console.warn(
      "浏览器不支持 ResizeObserver，使用 window resize 事件作为降级方案"
    );
  }

  // 8. 初始化渲染
  renderWatermark();
  window.addEventListener("resize", handleResize);

  // 8. 返回控制方法
  return {
    update: (newOptions) => {
      // 支持更新任意参数
      if (newOptions.watermarkText) watermarkText = newOptions.watermarkText;
      if (newOptions.color) color = newOptions.color;
      if (newOptions.fontSize) fontSize = newOptions.fontSize;
      if (newOptions.angle) angle = newOptions.angle;
      if (newOptions.opacity) opacity = newOptions.opacity;

      if (newOptions.mode) {
        currentMode = newOptions.mode === "count" ? "count" : "gap";
      }

      // 更新密度配置
      if (newOptions.density) {
        densityLevel = normalizeDensity(newOptions.density);
        fixedCount = fixedCountMap[densityLevel] || defaultFixedCountMap.medium;
      }
      renderWatermark();
    },
    remove: () => {
      observer.disconnect();
      window.removeEventListener("resize", handleResize);
      // 清理 ResizeObserver
      if (resizeObserver) {
        resizeObserver.disconnect();
        resizeObserver = null;
      }
      // 清理防抖定时器
      if (resizeTimer) {
        clearTimeout(resizeTimer);
        resizeTimer = null;
      }
      if (watermarkContainer && watermarkContainer.parentNode === container) {
        container.removeChild(watermarkContainer);
      }
    },
  };
}
